import type { PaymentMethod, Stripe, StripeElements } from '@stripe/stripe-js'
import { updateStripeOffSessionCardByRegionAndPurposeId, type LuxPlusOffSessionPaymentCard } from 'api/payment'
import config from 'constants/config'
import {
  arePaymentCardsLikelyTheSame,
  hasCompletePaymentCardDetails,
  preparePaymentCardIdentity,
  savedStripeCardToPaymentCardIdentity,
  stripePaymentMethodToPaymentCardIdentity,
  type PaymentCardIdentity,
} from 'lib/payment/cardIdentity'

export interface LuxPlusCheckoutCardBackupCandidate extends PaymentCardIdentity {
  requiresFingerprintMatch?: boolean
}

export type StripeLuxPlusOffSessionPaymentCard = LuxPlusOffSessionPaymentCard & { card: App.StripePaymentCardV2 }

interface PrepareLuxPlusCheckoutCardBackupCandidateInput {
  account: Pick<App.AuthAccount, 'email' | 'fullName'>
  elements: StripeElements
  stripe: Stripe
}

interface AppendLuxPlusBackupCardInput {
  currencyCode: string
  elements?: StripeElements
  paymentMethodId: string
  purposeId: string
  regionCode: string
  stripe: Stripe
}

export interface LuxPlusCheckoutCardBackupEligibilityInput {
  shouldShowBackupPaymentMethod: boolean
  hasActiveLuxPlusSubscription: boolean
  hasExistingLuxPlusBackupPaymentMethod?: boolean
  hasLuxPlusSubscriptionInCart: boolean
  isCardPaymentMethod: boolean
  selectedCheckoutCard?: LuxPlusCheckoutCardBackupCandidate
  currentLuxPlusPaymentMethod?: LuxPlusCheckoutCardBackupCandidate
}

export function stripePaymentCardToLuxPlusCheckoutCardBackupCandidate(card: App.StripePaymentCardV2): LuxPlusCheckoutCardBackupCandidate {
  return savedStripeCardToPaymentCardIdentity(card)
}

export function luxPlusOffSessionCardToLuxPlusCheckoutCardBackupCandidate(card: LuxPlusOffSessionPaymentCard): LuxPlusCheckoutCardBackupCandidate {
  return {
    paymentMethodId: card.paymentMethod,
    fingerprint: card.card?.fingerprint,
    brand: card.card?.brand,
    last4: card.card?.last4,
    funding: card.card?.funding,
    expMonth: card.card?.expMonth,
    expYear: card.card?.expYear,
  }
}

export function isStripeLuxPlusOffSessionPaymentCard(card: LuxPlusOffSessionPaymentCard): card is StripeLuxPlusOffSessionPaymentCard {
  return card.paymentProvider === 'stripe' && !!card.card
}

export function stripePaymentMethodToLuxPlusCheckoutCardBackupCandidate(paymentMethod: PaymentMethod): LuxPlusCheckoutCardBackupCandidate {
  return stripePaymentMethodToPaymentCardIdentity(paymentMethod)
}

export async function prepareLuxPlusCheckoutCardBackupCandidate(input: PrepareLuxPlusCheckoutCardBackupCandidateInput): Promise<LuxPlusCheckoutCardBackupCandidate> {
  return preparePaymentCardIdentity(input)
}

export async function appendLuxPlusBackupCard(input: AppendLuxPlusBackupCardInput): Promise<string> {
  const {
    currencyCode,
    elements,
    paymentMethodId,
    purposeId,
    regionCode,
    stripe,
  } = input

  const { clientSecret } = await updateStripeOffSessionCardByRegionAndPurposeId(regionCode, purposeId, {
    currency: currencyCode,
    brand: config.BRAND,
    paymentMethodType: 'card',
    metadata: {
      paymentMethodId,
      purpose: 'lux_plus',
      shouldAppendBackupOffSessionCard: true,
    },
  })

  const setupResult = elements ?
      await stripe.confirmSetup({
        clientSecret,
        redirect: 'if_required',
      }) :
      await stripe.confirmCardSetup(clientSecret)

  if (setupResult.error) {
    throw new Error(setupResult.error.message)
  }

  return paymentMethodId
}

export function hasMatchingLuxPlusCheckoutCardBackupCandidate(
  checkoutCard: LuxPlusCheckoutCardBackupCandidate,
  luxPlusPaymentMethod?: LuxPlusCheckoutCardBackupCandidate,
) {
  return arePaymentCardsLikelyTheSame(checkoutCard, luxPlusPaymentMethod)
}

export function shouldShowLuxPlusCheckoutCardBackupCheckbox(input: LuxPlusCheckoutCardBackupEligibilityInput) {
  const {
    shouldShowBackupPaymentMethod,
    hasActiveLuxPlusSubscription,
    hasExistingLuxPlusBackupPaymentMethod,
    hasLuxPlusSubscriptionInCart,
    isCardPaymentMethod,
    selectedCheckoutCard,
    currentLuxPlusPaymentMethod,
  } = input

  if (!shouldShowBackupPaymentMethod) return false
  if (!hasActiveLuxPlusSubscription) return false
  if (hasExistingLuxPlusBackupPaymentMethod) return false
  if (hasLuxPlusSubscriptionInCart) return false
  if (!isCardPaymentMethod) return false
  if (!selectedCheckoutCard?.paymentMethodId && !selectedCheckoutCard?.fingerprint) return false
  if (selectedCheckoutCard.requiresFingerprintMatch && !selectedCheckoutCard.fingerprint && !hasCompletePaymentCardDetails(selectedCheckoutCard)) return false

  return !hasMatchingLuxPlusCheckoutCardBackupCandidate(selectedCheckoutCard, currentLuxPlusPaymentMethod)
}
